import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle, XCircle, Award, Calendar, User, Clock,
  FileText, Loader2, Shield, ExternalLink, QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VerifyConstanciaPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    verifyConstancia();
  }, [code]);

  const verifyConstancia = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/recognition/constancias/verify/${code}`);
      setData(response.data);
    } catch (err) {
      setError('Error verificando la constancia');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const contentTypeNames = {
    curso: 'Curso',
    webinar: 'Webinar',
    masterclass: 'Master Class',
    diplomado: 'Diplomado',
    taller: 'Taller',
    ponencia: 'Ponencia',
    platica: 'Plática'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-gray-400">Verificando constancia...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error de Verificación</h1>
          <p className="text-gray-600 mb-6">{error || 'No se pudo verificar la constancia'}</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
          >
            Ir al inicio
          </Link>
        </div>
      </div>
    );
  }

  const { valid, constancia } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            Sistema de Verificación UPIM
          </div>
          <h1 className="text-3xl font-bold text-white">Verificación de Constancia</h1>
        </div>

        {/* Result Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Status Banner */}
          <div className={`p-6 ${valid ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
            <div className="flex items-center justify-center gap-3">
              {valid ? (
                <>
                  <CheckCircle className="w-10 h-10 text-white" />
                  <div className="text-white">
                    <p className="text-sm font-medium opacity-90">Estado</p>
                    <p className="text-2xl font-bold">CONSTANCIA VÁLIDA</p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-10 h-10 text-white" />
                  <div className="text-white">
                    <p className="text-sm font-medium opacity-90">Estado</p>
                    <p className="text-2xl font-bold">CONSTANCIA NO VÁLIDA</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {valid && constancia && (
            <div className="p-8">
              {/* Holder Info */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Titular</p>
                    <p className="text-xl font-bold text-gray-900">{constancia.holder_name}</p>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 inline-block">
                  <p className="text-xs text-amber-600 font-medium">NÚMERO DE AFILIACIÓN UPIM</p>
                  <p className="text-lg font-bold text-amber-800 tracking-wider">
                    {constancia.affiliation_number}
                  </p>
                </div>
              </div>

              {/* Content Info */}
              <div className="border-t border-gray-100 pt-6 mb-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
                  Detalles del Contenido
                </h3>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded mb-2">
                        {contentTypeNames[constancia.content_type] || constancia.content_type}
                      </span>
                      <h4 className="text-lg font-bold text-gray-900">
                        {constancia.content_title}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">Fecha del curso</span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {formatDate(constancia.content_date)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Fecha de emisión</span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {formatDate(constancia.issued_at)}
                  </p>
                </div>
              </div>

              {/* Quiz Badge */}
              {constancia.quiz_passed && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Examen aprobado</span>
                  </div>
                </div>
              )}

              {/* Verification Code */}
              <div className="border-t border-gray-100 pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Código de verificación</p>
                    <p className="text-lg font-mono font-bold text-gray-900 tracking-widest">{code}</p>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <QrCode className="w-5 h-5" />
                    <span className="text-sm">Verificado</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!valid && (
            <div className="p-8 text-center">
              <p className="text-gray-600 mb-4">
                La constancia con el código <strong className="font-mono">{code}</strong> no fue encontrada o no es válida.
              </p>
              <p className="text-sm text-gray-500">
                Si crees que esto es un error, por favor contacta a soporte.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm mb-4">
            Esta verificación fue realizada en el sistema oficial de UPIM
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300"
          >
            <ExternalLink className="w-4 h-4" />
            Visitar UPIM
          </Link>
        </div>
      </div>
    </div>
  );
}
